<template>
  <BottomSheet class="stc__first-time-message" :is-open="isDefaultSheetOpen" tablet-max-width="100%" @close="isDefaultSheetOpen = false">
    <template #header>
      <h4>Shop the collection</h4>
    </template>

    <span class="large">Select the items you'd like then add them all to your bag.</span>

    <div v-if="showFirstTimeImages" class="image-wrapper" data-testid="stc-first-time-images">
      <div v-for="(member, idx) in firstThreeMembers" :key="member.id || idx" class="image">
        <Picture
          v-if="getMemberImageUrl(member)"
          class="thumb-image"
          :image-url="getMemberImageUrl(member)"
          :alt="member.detail?.name || ''"
          :image-sizes-by-breakpoints="imageSizeByBreakpoint"
        />
        <Icon class="icon icon--confirmation" :icon="csgConfirmationFilled" size="m" theme="dark" />
      </div>
    </div>

    <template #footer>
      <Button variant="primary" expanded aria-label="Acknowledge and close instructions" @click="isDefaultSheetOpen = false"> Got it </Button>
    </template>
  </BottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import BottomSheet from '~/components/common/BottomSheet.vue';
import Button from '@atomic-ui/button/Button.vue';
import Picture from '@atomic-ui/picture/Picture';
import Icon from '@atomic-ui/icon/Icon.vue';
import { csgConfirmationFilled } from '@csgSvgIcons/icons';
import { useShopTheCollectionTabsStore } from '~/stores/useShopTheCollectionTabsStore';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useOncePerSession from '~/composables/utils/useOncePerSession';
import useUrlTemplate from '~/composables/useUrlTemplate';
import type { MemberProductMapProduct } from '~~/shared/globalTypes/product';

const store = useShopTheCollectionTabsStore();
const { activeTabMembers } = storeToRefs(store);

const { isMobile } = useDeviceType();
const { productUrlTemplate } = useUrlTemplate();
const { hasBeenUsed } = useOncePerSession('stc-first-time-message');

const isDefaultSheetOpen = ref<boolean>(false);
const imageSizeByBreakpoint = { small: 96, medium: 96, large: 96, xlarge: 96 };

onMounted(() => {
  if (!hasBeenUsed.value) {
    isDefaultSheetOpen.value = true;
  }
});

const firstThreeMembers = computed(() => {
  return activeTabMembers.value.slice(0, 3);
});

const showFirstTimeImages = computed(() => {
  return isMobile.value && activeTabMembers.value.length >= 3;
});

const getMemberImageUrl = (member: MemberProductMapProduct) => {
  const filePath = member?.imagery?.images?.[0]?.filePath;
  if (!filePath) return '';
  return productUrlTemplate ? productUrlTemplate + filePath : filePath;
};
</script>

<style lang="scss">
.stc__first-time-message {
  .pdp-bottom-sheet__header {
    padding: 24px 16px 8px;
  }

  .pdp-bottom-sheet__body {
    padding: 0 16px;
  }

  .pdp-bottom-sheet__header,
  .pdp-bottom-sheet__footer {
    border: none;
  }

  .image-wrapper {
    @include margin-top-xs;
    @include padding-xs;
    border: 1px solid $gray-5-color;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    .image {
      position: relative;
      width: 100%;
      aspect-ratio: 4 / 5;

      .icon {
        position: absolute;

        &.icon--confirmation {
          top: 4px;
          right: 4px;
        }
      }
    }
  }
}
</style>


